/* Pump engine — drumpump.com. Shared by Find My Pump and Shop by Chemical; contains no brand names.
 *
 * Reads only two things: product metafields (through the fmp-json product feed) and pump-chemicals.json.
 * Safety rules both tools apply (do not relax):
 *  - A pump is rated for a chemical only by its manufacturer's chart. The product names the chart with
 *    custom.rating_source and the column with custom.rating_key; the chart says which ratings count as rated and which
 *    mean "check with us".
 *  - A manufacturer's condition on a rating moves the pump to "check with us". When the only condition is a part the store
 *    sells and the pump accepts (custom.accepts_upgrades), the pump is "rated with a required upgrade" and is sold with that
 *    part; a pump built with the part (custom.includes_upgrades) is rated as sold. Several liquids and an upgrade: check with us.
 *  - A manufacturer's "do not use" for a chemical removes all of that manufacturer's pumps for it.
 *  - Flammable, combustible or unknown-flammability liquids, and liquids whose manufacturer note asks for a groundable
 *    pump, only get pumps that can be grounded (custom.grounding "included" or "customer") or are rated for gasoline;
 *    pumps that cannot be grounded are left out. Unknown flammability counts as flammable unless the customer answers "no".
 *  - A hose sold with the pump (custom.bundled_hose) must be named in the chemical's hose advice. With no advice, bundles
 *    are left out.
 *  - Compatibility is never inferred from materials, titles or vendors.
 *  - "Named on the manufacturer's product page" (only when a tool asks for it, never a rating): a pump whose Fluid tags carry
 *    the exact value of an everyday liquid (the chemical's "lt", from a reviewed list in the data build), when the pump's
 *    chart has no row for that liquid or the pump has no chart. Where a chart has a row, the chart decides. The flammability,
 *    groundable and hose rules above apply, and any manufacturer's "do not use" for the liquid blocks it.
 *  - Products with a missing or invalid required field are left out of both tools and listed on the product data check page.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.PumpEngine = root.PumpEngine || factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var E = {};
  var own = Object.prototype.hasOwnProperty;
  function arr(v) { return Array.isArray(v) ? v : []; }
  function has(v) { return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length); }
  function lc(v) { return v == null ? '' : String(v).toLowerCase(); }

  E.ROLES = ['pump', 'package'];                 // custom.wizard_role values the tools can show
  E.FLAMMABLE = ['flammable', 'combustible'];
  E.NO_HOSE = 'None';

  // ------------------------------------------------------------------ products
  E.inTools = function (p) { return !!p && p.enabled === true && E.ROLES.indexOf(p.role) >= 0; };
  E.hoseValid = function (p, data) { return p.bundled_hose === E.NO_HOSE || arr(data.hoses).indexOf(p.bundled_hose) >= 0; };
  // The hose sold with the pump, or null
  E.bundledHose = function (p) { return p.bundled_hose && p.bundled_hose !== E.NO_HOSE ? p.bundled_hose : null; };
  // Shown in the tools: switched on, a pump or package, and every field both tools rely on is filled in correctly
  E.usable = function (p, data) {
    return E.inTools(p) && has(p.containers) && has(p.power) && has(p.pump_type) && E.hoseValid(p, data);
  };
  // The manufacturer chart that rates this product, or null (no chart, or rating_source / rating_key not in the data)
  E.chart = function (p, data) {
    var s = p && p.rating_source && own.call(data.sources || {}, p.rating_source) ? data.sources[p.rating_source] : null;
    return s && p.rating_key && arr(s.keys).indexOf(p.rating_key) >= 0 ? s : null;
  };
  // How the pump is grounded (custom.grounding): 'included' = sold with the strap, 'customer' = metal body the customer
  // grounds itself, 'none' = cannot be grounded (plastic body, or a motor that is not rated for flammables).
  // Until custom.grounding is filled in, custom.groundable true still means "included" and anything else "none",
  // so the tools stay as cautious as they are today.
  E.GROUNDING = ['included', 'customer', 'none'];
  E.grounding = function (p) {
    var v = lc(p && p.grounding).trim();
    return E.GROUNDING.indexOf(v) >= 0 ? v : (p && p.groundable === true ? 'included' : 'none');
  };
  E.groundOk = function (p) { return E.grounding(p) !== 'none' || arr(p.fluid_tags).indexOf('gasoline') >= 0; };
  // Sorted first for a liquid that needs grounding: the pump arrives ready to ground
  E.groundReady = function (p) { return E.grounding(p) === 'included'; };

  // Everything wrong with one product, for the data check page. known (optional) lists the values the tools' questions
  // and filters cover: { containers: [], power: [], features: [], viscosity: [] }.
  // level: 'excluded' = left out of both tools; 'unrated' = shown, but never for a chemical; 'warning' = shown, check it.
  E.productIssues = function (p, data, known) {
    var out = [];
    function add(level, field, message) { out.push({ level: level, field: field, message: message }); }
    if (!E.inTools(p)) return out;
    if (!has(p.containers)) add('excluded', 'container_fits', 'Empty. Add every container the pump fits.');
    if (!has(p.power)) add('excluded', 'power_source', 'Empty.');
    if (!has(p.pump_type)) add('excluded', 'pump_type', 'Empty.');
    if (!has(p.bundled_hose)) add('excluded', 'bundled_hose', 'Empty. Use None when no hose is sold with the pump.');
    else if (!E.hoseValid(p, data)) add('excluded', 'bundled_hose', '"' + p.bundled_hose + '" is not one of: ' + [E.NO_HOSE].concat(arr(data.hoses)).join(', ') + '.');
    if (has(p.rating_source) || has(p.rating_key)) {
      var src = has(p.rating_source) && own.call(data.sources || {}, p.rating_source) ? data.sources[p.rating_source] : null;
      if (!has(p.rating_source)) add('unrated', 'rating_source', 'Empty, but rating_key is "' + p.rating_key + '". Fill both or neither.');
      else if (!src) add('unrated', 'rating_source', '"' + p.rating_source + '" has no chart in pump-chemicals.json. Charts: ' + Object.keys(data.sources || {}).join(', ') + '.');
      else if (!has(p.rating_key)) add('unrated', 'rating_key', 'Empty. Use the column of ' + src.label + '’s chart that rates this pump.');
      else if (arr(src.keys).indexOf(p.rating_key) < 0) add('unrated', 'rating_key', '"' + p.rating_key + '" is not a column in ' + src.label + '’s chart (check spelling and capitals).');
    }
    if (!has(p.grounding)) add('warning', 'grounding', 'Empty, so the pump falls back to Groundable: ' + (p.groundable === true ? 'grounded' : 'not groundable') + '. Fill it in: included, customer or none.');
    else if (E.GROUNDING.indexOf(lc(p.grounding).trim()) < 0) add('warning', 'grounding', '"' + p.grounding + '" is not one of: ' + E.GROUNDING.join(', ') + '.');
    else if (p.groundable !== true && p.groundable !== false) add('warning', 'groundable', 'Empty. Fill it in (true only for a pump sold with its grounding strap) so both fields agree.');
    else if ((lc(p.grounding).trim() === 'included') !== (p.groundable === true)) add('warning', 'groundable', 'Says ' + (p.groundable === true ? 'grounded' : 'not groundable') + ', but Grounding says "' + lc(p.grounding).trim() + '". The tools follow Grounding, so change whichever one is wrong.');
    if (!has(p.fluid_tags)) add('warning', 'fluid_tags', 'Empty. Find My Pump only recommends it for a named chemical.');
    if (!has(p.viscosity)) add('warning', 'viscosity_class', 'Empty. Find My Pump never recommends it when it asks how thick the liquid is.');
    if (arr(p.features).indexOf('preset-meter') >= 0 && arr(p.features).indexOf('meter') < 0) add('warning', 'features', 'Has preset-meter but not meter. A preset meter is a meter: add both.');
    ['accepts_upgrades', 'includes_upgrades'].forEach(function (field) {
      arr(p[field]).filter(function (id) { return !own.call(data.parts || {}, id); }).forEach(function (id) {
        add('warning', field, '"' + id + '" is not an upgrade part in pump-chemicals.json. Parts: ' + Object.keys(data.parts || {}).join(', ') + '.');
      });
    });
    arr(p.accepts_upgrades).filter(function (id) { return own.call(data.parts || {}, id) && !E.partFits(p, data, id); }).forEach(function (id) {
      add('warning', 'accepts_upgrades', '"' + id + '" is sold only for standard pumps and this pump is groundable, so it is never offered. Remove it, or check groundable.');
    });
    if (known) {
      var unknown = function (field, values, list, what) {
        values.filter(function (v) { return list.indexOf(v) < 0; }).forEach(function (v) { add('warning', field, '"' + v + '" is not a value ' + what + '.'); });
      };
      if (known.containers) unknown('container_fits', arr(p.containers), known.containers, 'the tools know; Shop by Chemical shows it as its own filter, Find My Pump never asks for it');
      if (known.power && has(p.power)) unknown('power_source', [p.power], known.power, 'the tools know; Shop by Chemical shows it as its own filter');
      if (known.features) unknown('features', arr(p.features), known.features, 'Find My Pump scores');
      if (known.viscosity) unknown('viscosity_class', arr(p.viscosity), known.viscosity, 'Find My Pump asks about');
      if (known.fluid_tags) unknown('fluid_tags', arr(p.fluid_tags), known.fluid_tags, 'the tools know (check spelling: lower case, hyphens)');
    }
    return out;
  };

  // ------------------------------------------------------------------ chemicals
  E.chemicals = function (data) { return arr(data && data.chemicals); };
  E.byId = function (chems, id) {
    if (!chems.__index) {
      var ix = {};
      chems.forEach(function (c) { ix[c.id] = c; });
      Object.defineProperty(chems, '__index', { value: ix, enumerable: false });
    }
    return own.call(chems.__index, id) ? chems.__index[id] : null;
  };
  // A CAS number with everything but the digits taken out, so 67-64-1, 67 64 1 and 67641 all compare equal.
  function casDigits(v) { return String(v == null ? '' : v).replace(/[^0-9]/g, ''); }
  // What was typed, read as a CAS number: digits and separators only, two digits or more. Anything else is a name.
  E.casQuery = function (query) {
    var q = String(query == null ? '' : query).trim();
    if (!/^[0-9][0-9\s-]*$/.test(q)) return '';  // a CAS number is digits and hyphens only, so 1.2 is a figure, not a number to look up
    var k = casDigits(q);
    return k.length >= 2 ? k : '';
  };
  // Chemicals whose CAS number starts with what was typed, the exact number first. The CAS box uses this on its own so a
  // stray word there shows nothing instead of the whole A–Z list; E.search folds the same matching in beside names.
  E.searchByCas = function (chems, query, limit) {
    var qc = E.casQuery(query);
    if (!qc) return [];
    var hit = [], pre = [];
    for (var i = 0; i < chems.length; i++) {
      var c = chems[i], k = c.cas ? casDigits(c.cas) : '';
      if (!k) continue;
      if (k === qc) hit.push(c);
      else if (k.indexOf(qc) === 0) pre.push(c);
    }
    pre.sort(function (a, b) { return casDigits(a.cas).localeCompare(casDigits(b.cas)) || String(a.n).localeCompare(String(b.n)); });
    return hit.concat(pre).slice(0, limit || 12);
  };
  // Every chemical that carries a CAS number, in number order, so the CAS box can be opened and read the way the name
  // box opens on A–Z. Built once per list; don't modify. Segments are compared as numbers, or 110-54-3 would sort
  // before 67-64-1.
  function casParts(v) {
    var p = String(v == null ? '' : v).split('-');
    return [parseInt(p[0], 10) || 0, parseInt(p[1], 10) || 0, parseInt(p[2], 10) || 0];
  }
  E.casList = function (chems) {
    if (!chems.__cas) {
      var list = chems.filter(function (c) { return !!c.cas; }).sort(function (a, b) {
        var x = casParts(a.cas), y = casParts(b.cas);
        return (x[0] - y[0]) || (x[1] - y[1]) || (x[2] - y[2]) || String(a.n).localeCompare(String(b.n));
      });
      Object.defineProperty(chems, '__cas', { value: list, enumerable: false });
    }
    return chems.__cas;
  };
  // The line above the CAS list. It says how many chemicals have a number, because most do not and an empty answer
  // further down should not read as a fault.
  E.casNote = function (count) { return count.toLocaleString('en-US') + ' chemicals have a CAS number, by number. Type to narrow the list.'; };
  // What the CAS box shows: nothing typed = every chemical with a number; a number = the matches; anything else = none,
  // so the box answers instead of falling back to names it was not asked for.
  E.browseByCas = function (chems, query) {
    var q = String(query == null ? '' : query).trim();
    if (!q) return E.casList(chems);
    return E.casQuery(q) ? E.searchByCas(chems, q, chems.length) : [];
  };
  // Name, common name or CAS number. Exact → starts with → word start → contains; entries rated by more charts first.
  // The CAS number is also carried as a common name, so the hyphenated form is matched there; the digits-only pass below
  // is what lets 67641 reach acetone as well. A CAS prefix scores under a name that starts with the same digits, so
  // typing 110 still puts "110 Ignitor Fuse Race Fuel" first.
  E.search = function (chems, query, limit) {
    var q = lc(query).trim();
    if (q.length < 2) return [];
    var word = new RegExp('(^|[^a-z0-9])' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    var qCas = E.casQuery(q);
    var scored = [];
    for (var i = 0; i < chems.length; i++) {
      var c = chems[i], n = lc(c.n), al = arr(c.a), s = 0, j;
      if (n === q) s = 100;
      else if (n.indexOf(q) === 0) s = 80;
      else {
        for (j = 0; j < al.length && s < 70; j++) { if (al[j] === q) s = 90; else if (al[j].indexOf(q) === 0) s = 70; }
        if (!s) {
          if (word.test(n)) s = 60;
          else if (n.indexOf(q) >= 0) s = 40;
          else for (j = 0; j < al.length && !s; j++) if (al[j].indexOf(q) >= 0) s = 30;
        }
      }
      if (qCas && c.cas) {
        var ck = casDigits(c.cas);
        if (ck === qCas) s = Math.max(s, 100);
        else if (ck && ck.indexOf(qCas) === 0) s = Math.max(s, 75);
      }
      if (s) scored.push({ c: c, s: s + Math.min(arr(c.src).length, 2) });
    }
    scored.sort(function (a, b) { return b.s - a.s || a.c.n.length - b.c.n.length; });
    return scored.slice(0, limit || 12).map(function (x) { return x.c; });
  };
  // Every chemical name A–Z (numbers first; leading brackets and symbols ignored). Brand and product names (no "az" in the
  // chemical file) are left out: they are found by typing. Built once per list; don't modify.
  function sortKey(c) { return lc(c.n).replace(/^[^a-z0-9]+/, ''); }
  E.alphabetical = function (chems) {
    if (!chems.__az) {
      var marked = chems.some(function (c) { return c.az; });
      var az = chems.filter(function (c) { return !marked || c.az; }).sort(function (a, b) {
        return sortKey(a).localeCompare(sortKey(b), 'en', { numeric: true, sensitivity: 'base' }) || String(a.n).localeCompare(String(b.n));
      });
      Object.defineProperty(chems, '__az', { value: az, enumerable: false });
    }
    return chems.__az;
  };
  // The line above the A–Z list, the same in every search box
  E.browseNote = function (count) { return count.toLocaleString('en-US') + ' chemicals, A–Z. Type to narrow the list or find a brand name.'; };
  // The chemical dropdown every search box shows: nothing typed = every chemical name A–Z; one character = the names that
  // start with it, A–Z; more = every match including brand names, best first (E.search).
  E.browse = function (chems, query) {
    var q = lc(query).trim();
    if (!q) return E.alphabetical(chems);
    if (q.length === 1) return E.alphabetical(chems).filter(function (c) { return sortKey(c).charAt(0) === q; });
    return E.search(chems, q, chems.length);
  };
  // The flame mark in chemical lists: liquids known to be flammable or combustible (unknown ones are flagged once picked)
  E.flameLabel = function (chem) { return !chem ? null : chem.f === 'flammable' ? 'Flammable' : chem.f === 'combustible' ? 'Combustible' : null; };
  E.flammabilityUnknown = function (chem) { return !!chem && E.FLAMMABLE.indexOf(chem.f) < 0 && chem.f !== 'neither'; };
  // answer: the customer's reply for an unknown liquid ('yes' | 'no' | 'unsure' | undefined)
  E.flammable = function (chem, answer) {
    if (E.FLAMMABLE.indexOf(chem.f) >= 0) return true;
    if (chem.f === 'neither') return false;
    return answer !== 'no';
  };
  E.needsGroundable = function (chem, answer) { return E.flammable(chem, answer) || !!chem.gr; };
  // Manufacturer notes for one chart; kinds: any of 'do-not-use', 'condition', 'warning', 'recommendation', 'info'
  E.notes = function (chem, source, kinds) {
    return arr(chem && chem.notes).filter(function (n) { return (!source || n.s === source) && (!kinds || kinds.indexOf(n.k) >= 0); });
  };
  function forKey(n, key) { return (!arr(n.keys).length || n.keys.indexOf(key) >= 0) && arr(n.exceptKeys).indexOf(key) < 0; }
  // Condition notes on one chart column: a note applies to every column unless it lists keys (only those) or exceptKeys (all but those)
  E.conditionNotes = function (chem, source, key) {
    return E.notes(chem, source, ['condition']).filter(function (n) { return forKey(n, key); });
  };
  E.conditions = function (chem, source, key) { return E.conditionNotes(chem, source, key).map(function (n) { return n.t; }); };
  E.warnings = function (chem, source, key) {
    return E.notes(chem, source, ['warning']).filter(function (n) { return forKey(n, key); }).map(function (n) { return n.t; });
  };

  // ------------------------------------------------------------------ upgrade parts
  // Part products in the feed carry custom.upgrade_part: { part id: product }
  E.PART_ROLE = 'part';
  // A part sold only in a standard version (fits_groundable: false) would replace a groundable pump's groundable part
  E.partFits = function (p, data, id) {
    var d = own.call(data.parts || {}, id) ? data.parts[id] : null;
    return !(d && d.fits_groundable === false && E.grounding(p) === 'included');
  };
  E.partsCatalog = function (products) {
    var out = {};
    arr(products).forEach(function (p) { if (p && p.role === E.PART_ROLE && p.upgrade_part && p.variant_id && !own.call(out, p.upgrade_part)) out[p.upgrade_part] = p; });
    return out;
  };
  // For the data check page: every upgrade part in the data with the product that sells it and the pumps that accept
  // or include it, plus products whose upgrade fields the tools cannot use. issues: [{ p, field, message }]
  E.partsReport = function (products, data) {
    var catalog = E.partsCatalog(products), parts = data.parts || {}, issues = [], seen = {};
    var rows = Object.keys(parts).map(function (id) {
      return { id: id, label: parts[id].label, source: parts[id].source, product: catalog[id] || null, accepted: [], included: [] };
    });
    function row(id) { return rows.filter(function (r) { return r.id === id; })[0]; }
    arr(products).forEach(function (p) {
      if (p && p.role === E.PART_ROLE && !p.upgrade_part) issues.push({ p: p, field: 'upgrade_part', message: 'Empty, but wizard_role is part. Fill in the upgrade part it sells, or change wizard_role.' });
      if (!p || !p.upgrade_part) return;
      if (p.role !== E.PART_ROLE) issues.push({ p: p, field: 'wizard_role', message: 'Has upgrade_part "' + p.upgrade_part + '" but wizard_role is ' + (p.role ? '"' + p.role + '"' : 'empty') + '. Set it to ' + E.PART_ROLE + ', or clear upgrade_part.' });
      else if (!own.call(parts, p.upgrade_part)) issues.push({ p: p, field: 'upgrade_part', message: '"' + p.upgrade_part + '" is not an upgrade part in pump-chemicals.json. Parts: ' + Object.keys(parts).join(', ') + '.' });
      else if (!p.variant_id) issues.push({ p: p, field: 'variant', message: 'No variant to add to the cart.' });
      else if (own.call(seen, p.upgrade_part)) issues.push({ p: p, field: 'upgrade_part', message: 'Another product already sells "' + p.upgrade_part + '" (' + seen[p.upgrade_part].title + '). The tools use that one.' });
      if (p.role === E.PART_ROLE && own.call(parts, p.upgrade_part) && p.variant_id && !own.call(seen, p.upgrade_part)) seen[p.upgrade_part] = p;
    });
    arr(products).filter(E.inTools).forEach(function (p) {
      arr(p.accepts_upgrades).forEach(function (id) { var r = row(id); if (r && E.partFits(p, data, id)) r.accepted.push(p); });  // a part that does not fit is flagged on the pump
      arr(p.includes_upgrades).forEach(function (id) { var r = row(id); if (r) r.included.push(p); });
    });
    rows.forEach(function (r) {
      if (!r.product && r.accepted.length) r.missing = 'No product sells this part, so the ' + r.accepted.length + ' pump' + (r.accepted.length === 1 ? '' : 's') + ' that accept it show "check with us" instead.';
    });
    return { rows: rows, issues: issues };
  };
  // The part, as the tools show it: { id, label, note, product }
  E.part = function (data, catalog, id) {
    var d = own.call(data.parts || {}, id) ? data.parts[id] : null;
    return d && catalog && own.call(catalog, id) ? { id: id, label: d.label, note: d.note || '', product: catalog[id] } : null;
  };
  E.sourceLabels = function (data, ids) {
    return arr(ids).map(function (id) { return own.call(data.sources || {}, id) ? data.sources[id].label : id; });
  };

  // ------------------------------------------------------------------ rating
  // One product for one chemical: null (never shown) or { status: 'rated' | 'upgrade' | 'check', source, label, key, rating,
  // rank, reasons: [condition text], parts: [part id] (status 'upgrade'), included: [part id], warnings: [text] }.
  // catalog: E.partsCatalog(products); without it no upgrade is offered.
  E.rate = function (p, chem, data, answer, catalog) {
    if (!chem || !E.usable(p, data)) return null;
    var src = E.chart(p, data);
    if (!src) return null;
    if (E.notes(chem, p.rating_source, ['do-not-use']).length) return null;
    var rating = ((chem.r || {})[p.rating_source] || {})[p.rating_key];
    var rated = arr(src.rated).indexOf(rating) >= 0, fair = !rated && arr(src.check).indexOf(rating) >= 0;
    if (!rated && !fair) return null;
    var hose = E.bundledHose(p);
    if (hose && arr(chem.h).indexOf(hose) < 0) return null;
    if (E.needsGroundable(chem, answer) && !E.groundOk(p)) return null;
    var parts = data.parts || {}, includes = arr(p.includes_upgrades), accepts = arr(p.accepts_upgrades);
    function built(id) { return includes.some(function (x) { return x === id || (own.call(parts, x) && arr(parts[x].covers).indexOf(id) >= 0); }); }
    var blocking = fair ? [src.check_note || 'rated ' + rating] : [], met = [], need = [], included = [];
    E.conditionNotes(chem, p.rating_source, p.rating_key).forEach(function (n) {
      var ids = arr(n.upgrade);
      if (!ids.length) { blocking.push(n.t); return; }
      var missing = ids.filter(function (id) { if (built(id)) { if (included.indexOf(id) < 0) included.push(id); return false; } return true; });
      if (!missing.length) return;  // this pump is built with the part
      if (missing.some(function (id) { return accepts.indexOf(id) < 0 || !E.part(data, catalog, id) || !E.partFits(p, data, id); })) { blocking.push(n.t); return; }
      met.push(n.t);
      missing.forEach(function (id) { if (need.indexOf(id) < 0) need.push(id); });
    });
    // one part can cover another (a fluid path upgrade includes the tap o-rings): order only the larger one
    need = need.filter(function (id) { return !need.some(function (o) { return o !== id && own.call(parts, o) && arr(parts[o].covers).indexOf(id) >= 0; }); });
    var status = blocking.length ? 'check' : need.length ? 'upgrade' : 'rated';
    return {
      status: status, source: p.rating_source, label: src.label, key: p.rating_key, rating: rating,
      rank: (src.rank || {})[rating] || 0,
      reasons: status === 'check' ? blocking.concat(met) : status === 'upgrade' ? met : [],
      parts: status === 'upgrade' ? need : [], included: included,
      warnings: E.warnings(chem, p.rating_source, p.rating_key)
    };
  };

  // One product for one chemical, when no chart rates it: null or { status: 'listed', tag, label }. The pump is named for the
  // liquid on its manufacturer's product page (an exact value in custom.fluid_tags that the chemical lists in "lt"). Never for a
  // pump whose chart has a row for the chemical (a missing value there means "not recommended"), and never when any
  // manufacturer says not to use its pumps with it.
  E.listed = function (p, chem, data, answer) {
    if (!chem || !arr(chem.lt).length || !E.usable(p, data)) return null;
    if (has(p.rating_source) && own.call(chem.r || {}, p.rating_source)) return null;
    if (E.notes(chem, null, ['do-not-use']).length) return null;
    var tags = arr(p.fluid_tags), tag = arr(chem.lt).filter(function (t) { return tags.indexOf(t) >= 0; })[0];
    if (!tag) return null;
    var hose = E.bundledHose(p);
    if (hose && arr(chem.h).indexOf(hose) < 0) return null;
    if (E.needsGroundable(chem, answer) && !E.groundOk(p)) return null;
    return { status: 'listed', tag: tag, label: ((data.listed || {})[tag] || {}).label || tag };
  };

  // Several chemicals: a product must be rated for each one on its own (manufacturers rate chemicals, not blends). An upgrade
  // changes the pump's materials, so with several liquids a pump that needs one goes to "check with us".
  // answers: { chemical id: flammability answer }. Returns { results: rated as sold, upgrades: rated with a required upgrade,
  // askUs: check with us, listed: named on the product page (only with opts.listed) }. Items: { p, rating, rank, sources,
  // reasons: [{ chem, source, text }], parts: [part], included: [part id], warnings: [{ chem, source, text }], listed:
  // [{ chem, tag, label }] }, best rank first, then price, then title.
  // opts.listed: also return pumps named on their product page. With several liquids such a pump must be rated as sold or
  // named for every one of them; a condition or upgrade for any of them leaves it out.
  E.evaluate = function (products, chems, data, answers, opts) {
    var results = [], upgrades = [], askUs = [], listed = [], catalog = E.partsCatalog(products), withListed = !!(opts && opts.listed);
    if (!chems.length) return { results: results, upgrades: upgrades, askUs: askUs, listed: listed };
    products.forEach(function (p) {
      var worst = null, check = false, upgrade = false, sources = [], reasons = [], need = [], included = [], warnings = [], named = [];
      for (var i = 0; i < chems.length; i++) {
        var answer = (answers || {})[chems[i].id], r = E.rate(p, chems[i], data, answer, catalog);
        if (!r) {
          var l = withListed ? E.listed(p, chems[i], data, answer) : null;
          if (!l) return;
          named.push({ chem: chems[i], tag: l.tag, label: l.label });
          continue;
        }
        if (r.status !== 'rated') {
          if (r.status === 'check') check = true; else upgrade = true;
          r.reasons.forEach(function (t) { reasons.push({ chem: chems[i], source: r.source, text: t }); });
        }
        r.parts.forEach(function (id) { if (need.indexOf(id) < 0) need.push(id); });
        r.included.forEach(function (id) { if (included.indexOf(id) < 0) included.push(id); });
        r.warnings.forEach(function (t) { warnings.push({ chem: chems[i], source: r.source, text: t }); });
        if (sources.indexOf(r.source) < 0) sources.push(r.source);
        if (!worst || r.rank < worst.rank) worst = r;
      }
      if (named.length) {
        if (check || upgrade) return;
        listed.push({ p: p, rating: null, rank: 0, sources: sources, reasons: [], included: included, warnings: warnings, parts: [], listed: named });
        return;
      }
      if (upgrade && chems.length > 1) check = true;
      var item = { p: p, rating: worst.rating, rank: worst.rank, sources: sources, reasons: reasons, included: included, warnings: warnings,
        parts: check ? [] : need.map(function (id) { return E.part(data, catalog, id); }) };
      (check ? askUs : upgrade ? upgrades : results).push(item);
    });
    // A liquid that needs grounding puts the pumps sold with their strap first: the customer grounds the rest itself
    var needsGround = chems.some(function (c) { return E.needsGroundable(c, (answers || {})[c.id]); });
    var ready = function (it) { return needsGround && E.groundReady(it.p) ? 1 : 0; };
    var order = function (a, b) { return (ready(b) - ready(a)) || (b.rank - a.rank) || (a.p.price - b.p.price) || (a.p.title || '').localeCompare(b.p.title || ''); };
    results.sort(order); upgrades.sort(order); askUs.sort(order); listed.sort(order);
    return { results: results, upgrades: upgrades, askUs: askUs, listed: listed };
  };

  // ------------------------------------------------------------------ cart
  // "Add both to cart": the pump and its upgrade parts in one request; the caller opens the cart. The cart is read before the
  // request and again after a failure, because a cart holding the pump without its upgrade is what the customer must be told.
  // Resolves { ok, cartRead, pumpWithoutUpgrade, partOnly, morePumpsThanUpgrades }.
  E.addTogether = function (root, pumpVariant, partVariants, fetchFn) {
    var f = fetchFn || fetch, parts = arr(partVariants);
    function readCart() {
      return f(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
        .then(function (cart) {
          var q = {};
          arr(cart && cart.items).forEach(function (i) { var k = String(i.variant_id != null ? i.variant_id : i.id); q[k] = (q[k] || 0) + (Number(i.quantity) || 0); });
          return q;
        })
        .catch(function () { return null; });
    }
    function qty(cart, id) { return (cart && cart[String(id)]) || 0; }
    function more(cart) { return !!cart && parts.some(function (id) { return qty(cart, pumpVariant) > qty(cart, id); }); }
    var items = [pumpVariant].concat(parts).map(function (id) { return { id: id, quantity: 1 }; });
    return readCart().then(function (before) {
      return f(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) {
          if (!r.ok) { var refused = new Error('cart ' + r.status); refused.answered = true; throw refused; }
          // a pump already in the cart: after this add there are more pumps than upgrades
          return { ok: true, cartRead: !!before, pumpWithoutUpgrade: false, partOnly: false, morePumpsThanUpgrades: more(before) };
        })
        .catch(function (err) {
          var answered = !!(err && err.answered);  // the store refused the add; otherwise the answer was lost on the way
          return readCart().then(function (after) {
            var none = { ok: false, cartRead: false, pumpWithoutUpgrade: false, partOnly: false, morePumpsThanUpgrades: false };
            if (!after) return none;
            if (before) {
              // what this click added
              var added = function (id) { return qty(after, id) > qty(before, id); };
              var pumpAdded = added(pumpVariant), partsAdded = parts.map(added);
              if (pumpAdded && partsAdded.indexOf(false) < 0) return { ok: true, cartRead: true, pumpWithoutUpgrade: false, partOnly: false, morePumpsThanUpgrades: more(after) };
              return { ok: false, cartRead: true, pumpWithoutUpgrade: pumpAdded, partOnly: !pumpAdded && partsAdded.indexOf(true) >= 0, morePumpsThanUpgrades: more(after) };
            }
            // no cart from before the click: judge only by what the cart holds now
            var has = function (id) { return qty(after, id) > 0; };
            if (has(pumpVariant) && parts.some(function (id) { return !has(id); })) return { ok: false, cartRead: true, pumpWithoutUpgrade: true, partOnly: false, morePumpsThanUpgrades: more(after) };
            if (!answered && has(pumpVariant) && parts.every(has)) return { ok: true, cartRead: true, pumpWithoutUpgrade: false, partOnly: false, morePumpsThanUpgrades: more(after) };
            return none;  // cannot tell what was added: the customer is asked to check the cart
          });
        });
    });
  };
  // What to tell the customer after "Add both to cart": null means open the cart
  E.cartMessage = function (res) {
    var extra = 'Each pump needs its own upgrade for this liquid: review your cart before you order, or contact us.';
    if (res.ok) return res.morePumpsThanUpgrades ? 'Added. Your cart now has more of this pump than of its required upgrade. ' + extra : null;
    if (res.pumpWithoutUpgrade) return 'Your cart has this pump but not its required upgrade, which could not be added. Add the upgrade before you order, or contact us. Do not use the pump with this liquid without it.';
    if (res.partOnly) return 'Only the upgrade was added: the pump could not be added. ' + (res.morePumpsThanUpgrades ? 'Your cart already has this pump without enough upgrades. ' + extra : 'Please try again or contact us.');
    if (!res.cartRead) return 'The pump and its upgrade could not be added together. Check your cart before you order, or contact us.';
    return res.morePumpsThanUpgrades ? 'Nothing was added, and your cart already has this pump without enough upgrades. ' + extra
                                     : 'Nothing was added: the pump and its upgrade could not be added to your cart together. Please try again or contact us.';
  };

  return E;
});
